<script lang="ts" setup>
import type { Menu } from '@/services/shop'
import { AsyncOperationState } from '@/stores/lib/AsyncOperationState'

import { ref } from 'vue'

import ProductImage from '@/components/ProductImage.vue'
import Button from '@/components/Button.vue'
import Loading from '@/components/Loading.vue'
import ErrorMessage from '@/components/ErrorMessage.vue'

withDefaults(
  defineProps<{
    categories: Menu | null
    loadState: AsyncOperationState
    showTitle?: boolean
    showSearch?: boolean
  }>(),
  {
    showTitle: true,
    showSearch: true,
  },
)

const emit = defineEmits<{
  (e: 'selectCategory', handle: string): void
  (e: 'search', query: string): void
}>()

const q = ref('')
</script>

<template>
  <div
    v-if="loadState === AsyncOperationState.DONE"
    class="CategoryList Stack Stack--tiny"
  >
    <h4 v-if="showTitle">Shop now</h4>
    <form
      v-if="showSearch"
      class="CategoryList__InputContainer"
      @submit.prevent="emit('search', q)"
    >
      <input
        ref="searchInput"
        v-model="q"
        type="search"
        name="q"
        autocomplete="off"
        autocapitalize="none"
        autofocus
        placeholder="Search products…"
        class="CategoryList__Input"
      />
      <Button>Search</Button>
    </form>
    <div class="CategoryList__Grid">
      <div
        v-for="menuItem in categories?.items"
        :key="menuItem.id"
        class="CategoryList__Item"
        @click="emit('selectCategory', menuItem.resource.handle)"
      >
        <ProductImage
          :alt="menuItem.title"
          :url="menuItem.resource.image?.url"
          class="CategoryList__Image"
        />
        <p>{{ menuItem.title }}</p>
      </div>
    </div>
  </div>
  <div v-else-if="loadState === AsyncOperationState.IN_PROGRESS">
    <Loading />
  </div>
  <div v-else-if="loadState === AsyncOperationState.ERRORED">
    <ErrorMessage>
      <p>We couldn't load the categories.</p>
    </ErrorMessage>
  </div>
</template>

<style>
.CategoryList {
  > h4 {
    margin: 0;
  }
}

.CategoryList__Grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  align-items: start;
  gap: 16px;

  @media (--mq-phablet-and-up) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (--mq-tablet-and-up) {
    grid-template-columns: repeat(5, 1fr);
  }

  @media (--mq-landscape-tablet-and-up) {
    grid-template-columns: repeat(6, 1fr);
  }
}

.CategoryList__Item {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  cursor: pointer;
  transition: opacity 0.15s ease-in-out;

  &:hover {
    opacity: 0.75;
  }

  > p {
    margin: 0;
  }
}

.CategoryList__Image {
  width: 100%;
  height: auto;
  aspect-ratio: 1/1;
  object-fit: cover;
  border-radius: 4px;
  background-color: var(--color-white);
}

.CategoryList__Title {
  margin: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
}

.CategoryList__Input[type='search'] {
  background-image: url('@/assets/icons/search.svg');
  background-size: 20px 20px;
  background-position: 8px center;
  background-repeat: no-repeat;
  padding-left: 36px;
  width: 100%;

  /* clears the ‘X’ from Internet Explorer */
  &::-ms-clear {
    display: none;
    width: 0;
    height: 0;
  }

  &::-ms-reveal {
    display: none;
    width: 0;
    height: 0;
  }

  /* clears the ‘X’ from Chrome */
  &::-webkit-search-decoration,
  &::-webkit-search-cancel-button,
  &::-webkit-search-results-button,
  &::-webkit-search-results-decoration {
    display: none;
  }
}

.CategoryList__InputContainer {
  position: relative;
  display: flex;
  gap: 4px;
}
</style>
